import React from 'react';
import * as Sentry from "@sentry/react";
import { Entity, EntityMatch } from '@/domain/document-processing/types/entity-types';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ErrorFallback } from '@/components/error-boundary/ErrorFallback';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Info, CheckCircle2, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EntityAuditModalProps {
  entity: Entity | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EntityAuditModal({ entity, isOpen, onOpenChange }: EntityAuditModalProps) {
  if (!entity) return null;

  // Blank names are marked approved without a screening call; show them as not screened.
  const reviewStatus = entity.reviewStatus === 'flagged' ? 'flagged'
    : entity.reviewStatus === 'approved' && entity.name?.trim() ? 'approved'
    : 'pending review';
  const threshold = entity.matchDetails?.[0]?.richDetails?.threshold || 0.5;
  const highestConfidence = entity.matchDetails?.length 
    ? Math.max(...entity.matchDetails.map(match => match.confidence))
    : 0;

  const getStatusConfig = (status: string) => {
    switch(status) {
      case 'approved':
        return {
          icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
          title: 'No match above threshold',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-800',
          statusDisplay: (
            <p className="text-sm font-medium text-green-600 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" />
              No match above threshold
            </p>
          )
        };
      case 'flagged':
        return {
          icon: <AlertTriangle className="h-5 w-5 text-red-500" />,
          title: 'Potential match: review required',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          statusDisplay: (
            <p className="text-sm font-medium text-red-600 flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" />
              Potential match
            </p>
          )
        };
      default:
        return {
          icon: <Clock className="h-5 w-5 text-amber-500" />,
          title: 'Not screened',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          textColor: 'text-amber-800',
          statusDisplay: (
            <p className="text-sm font-medium text-amber-600 flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Not screened
            </p>
          )
        };
    }
  };

  const statusConfig = getStatusConfig(reviewStatus);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {statusConfig.icon}
            <span>{statusConfig.title}: {entity.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {/* Entity Summary */}
          <div className={cn(
            "rounded-md p-4 border",
            statusConfig.bgColor,
            statusConfig.borderColor
          )}>
            <h3 className={cn("text-lg font-medium mb-2", statusConfig.textColor)}>
              Entity Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Type</p>
                <p className="text-sm font-medium">{entity.type}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Name</p>
                <p className="text-sm font-medium">{entity.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Source</p>
                <p className="text-sm font-medium">{entity.sourceDocument} {entity.pageNumber && `(p.${entity.pageNumber})`}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Status</p>
                {statusConfig.statusDisplay}
              </div>
            </div>
          </div>

          {/* Enhanced Entity Data - show if available */}
          {(entity.nameVariations?.length > 0 || entity.addresses?.length > 0 || entity.contacts?.length > 0 || entity.relationships?.length > 0) && (
            <div>
              <h3 className="text-lg font-medium mb-2">Enhanced Data</h3>
              
              {/* Name Variations */}
              {entity.nameVariations?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium mb-1">Name Variations</h4>
                  <div className="flex flex-wrap gap-1 p-2 rounded-md border border-gray-200">
                    {entity.nameVariations.map((variation, idx) => (
                      <Badge key={idx} variant="secondary">
                        {variation}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Addresses */}
              {entity.addresses?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium mb-1">Addresses</h4>
                  <div className="space-y-2">
                    {entity.addresses.map((address, idx) => (
                      <div key={idx} className="p-2 rounded-md border border-gray-200">
                        <p className="text-sm">{address.full}</p>
                        {address.type && <p className="text-xs text-gray-500">Type: {address.type}</p>}
                        {address.components && (
                          <p className="text-xs text-gray-500 mt-1">
                            {address.components.city && `${address.components.city}, `}
                            {address.components.region && `${address.components.region}, `}
                            {address.components.country && address.components.country}
                            {address.components.postalCode && ` ${address.components.postalCode}`}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Contacts */}
              {entity.contacts?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium mb-1">Contacts</h4>
                  <div className="space-y-2">
                    {entity.contacts.map((contact, idx) => (
                      <div key={idx} className="p-2 rounded-md border border-gray-200">
                        <p className="text-sm font-medium">{contact.name}</p>
                        {contact.role && <p className="text-xs">Role: {contact.role}</p>}
                        {contact.email && <p className="text-xs">Email: {contact.email}</p>}
                        {contact.phone && <p className="text-xs">Phone: {contact.phone}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Relationships */}
              {entity.relationships?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium mb-1">Relationships</h4>
                  <div className="space-y-2">
                    {entity.relationships.map((rel, idx) => (
                      <div key={idx} className="p-2 rounded-md border border-gray-200">
                        <p className="text-sm font-medium">{rel.relationship}</p>
                        <p className="text-xs">With entity: {rel.targetEntityId}</p>
                        {rel.context && <p className="text-xs text-gray-500">{rel.context}</p>}
                        <p className="text-xs mt-1">Confidence: {(rel.confidence * 100).toFixed(0)}%</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Match Summary - Only show for flagged items */}
          {reviewStatus === 'flagged' && (
            <>
              {/* Match Details - Only show for flagged items */}
              <div>
                <h3 className="text-lg font-medium mb-2">Match Details</h3>
                <div className="space-y-4">
                  {entity.matchDetails?.length > 0 ? (
                    entity.matchDetails.map((match, index) => (
                      <MatchDetailItem key={index} match={match} index={index} threshold={threshold} />
                    ))
                  ) : (
                    <div className="text-sm p-4 border border-red-200 bg-red-50 rounded-md">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                        <p className="font-medium text-red-800">Potential match</p>
                      </div>
                      <p className="text-red-700">
                        This party returned a potential match during screening.
                        Compare it with the matching record before deciding whether it is the same party.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Approved Summary - Only show for approved items */}
          {reviewStatus === 'approved' && (
            <div className="bg-green-50 p-4 rounded-md border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <h3 className="text-lg font-medium text-green-800">No match above threshold</h3>
              </div>
              <p className="text-sm text-green-700">
                This name was screened against sanctions and watchlist data and returned no match above the
                screening threshold. A screening result is not a clearance decision.
              </p>
            </div>
          )}

          {/* Pending Summary - Only show for pending items */}
          {reviewStatus === 'pending review' && (
            <div className="bg-amber-50 p-4 rounded-md border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-5 w-5 text-amber-500" />
                <h3 className="text-lg font-medium text-amber-800">Not screened</h3>
              </div>
              <p className="text-sm text-amber-700">
                This party has no screening result. Screening may not have finished or may have failed.
                Do not rely on it as clear.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// TypeScript fix for Sentry error boundary
export default EntityAuditModal;

interface MatchDetailItemProps {
  match: EntityMatch;
  index: number;
  threshold: number;
}

function MatchDetailItem({ match, index, threshold }: MatchDetailItemProps) {
  const exceedsThreshold = match.confidence > threshold;
  const richDetails = match.richDetails || {};
  
  return (
    <div className={cn(
      "border rounded-md p-4",
      exceedsThreshold ? "border-red-200 bg-red-50" : "border-gray-200"
    )}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium flex items-center gap-2">
          {exceedsThreshold ? (
            <AlertTriangle className="h-4 w-4 text-red-500" />
          ) : (
            <Info className="h-4 w-4 text-gray-500" />
          )}
          Match #{index + 1}: {richDetails.name || 'Unknown Entity'}
        </h4>
        <Badge variant={exceedsThreshold ? "destructive" : "outline"}>
          {(match.confidence * 100).toFixed(1)}% confidence
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mt-3">
        <div>
          <p className="text-xs font-medium text-gray-500">Schema</p>
          <p className="text-sm">{richDetails.schema || 'Unknown'}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500">Source</p>
          <p className="text-sm">{match.source || 'Unknown'}</p>
        </div>
        
        {match.details && (
          <div className="col-span-2">
            <p className="text-xs font-medium text-gray-500">Details</p>
            <p className="text-sm">{match.details}</p>
          </div>
        )}
        
        {richDetails.topics?.length > 0 && (
          <div className="col-span-2">
            <p className="text-xs font-medium text-gray-500">Topics</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {richDetails.topics.map((topic: string, i: number) => (
                <Badge key={i} variant="secondary" className="text-xs">
                  {topic}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {richDetails.countries?.length > 0 && (
          <div className="col-span-2">
            <p className="text-xs font-medium text-gray-500">Countries</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {richDetails.countries.map((country: string, i: number) => (
                <Badge key={i} variant="outline" className="text-xs">
                  {country}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {richDetails.sanctions?.length > 0 && (
          <div className="col-span-2">
            <p className="text-xs font-medium text-gray-500">Sanctions Programs</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {richDetails.sanctions.map((sanction: string, i: number) => (
                <Badge key={i} variant="destructive" className="text-xs">
                  {sanction}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {exceedsThreshold && (
          <div className="col-span-2 mt-2 p-2 bg-red-100 rounded border border-red-200">
            <p className="text-xs font-medium text-red-800 flex items-center">
              <AlertTriangle className="h-3 w-3 mr-1" />
              This match exceeds the confidence threshold of {(threshold * 100).toFixed(1)}%
            </p>
          </div>
        )}
      </div>
    </div>
  );
} 